/**
 * Navigation Alpine.js Integration
 * Handles mobile sliding menu and desktop mega menu interactions
 */

document.addEventListener('alpine:init', () => {

	// =========================================================================
	// Mobile Sliding Menu Component
	// =========================================================================

	Alpine.data('mobileNavSlider', () => ({
		activeSubmenu: null,
		prevSubmenu: null,
		submenus: [],

		init() {
			this.$nextTick(() => {
				this.parseMenuItems();
			});
		},

		/**
		 * Parse the walker-generated menu HTML to extract submenu panels.
		 * Reads .wrapper-level-0 items that have .sub-menu children,
		 * and builds an array of { title, html } for the sliding panels.
		 */
		parseMenuItems() {
			const menuContainer = this.$el.querySelector('.mobile__menu-container');
			if (!menuContainer || menuContainer.dataset.parsed) return;
			menuContainer.dataset.parsed = 'true';

			const topLevelItems = menuContainer.querySelectorAll(':scope .wrapper-level-0');
			const submenus = [];

			topLevelItems.forEach((wrapper, index) => {
				const menuItemContainer = wrapper.querySelector(':scope > .menu-item__container');
				const subMenu = wrapper.querySelector(':scope > .sub-menu');

				if (menuItemContainer && subMenu) {
					// Get the title from the menu item link
					const titleEl = menuItemContainer.querySelector('.menu-item-text');
					const title = titleEl ? titleEl.textContent.trim() : '';

					// Clone the submenu inner content
					const inner = subMenu.querySelector('.inner');
					const html = inner ? inner.innerHTML : subMenu.innerHTML;

					submenus.push({ title, html });

					// Replace the menu item click behavior
					// Remove the existing arrow SVG and add a right arrow
					const existingSvg = menuItemContainer.querySelector('svg');
					if (existingSvg) {
						existingSvg.remove();
					}

					// Add right-pointing arrow
					const arrow = document.createElement('span');
					arrow.classList.add('mobile-nav-arrow');
					arrow.innerHTML = `<svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
						<path d="M7.5 5L12.5 10L7.5 15" stroke="currentColor" stroke-width="1" stroke-linecap="round" stroke-linejoin="round"/>
					</svg>`;
					menuItemContainer.appendChild(arrow);

					// Store the submenu index
					const submenuIndex = submenus.length - 1;

					// Make the entire container clickable to open the submenu
					menuItemContainer.addEventListener('click', (e) => {
						// If clicked on the arrow or the container (not the link itself), open submenu
						if (e.target.tagName !== 'A') {
							e.preventDefault();
							e.stopPropagation();
							this.openSubmenu(submenuIndex);
						}
					});

					// Also intercept the link click if it's just "#" or empty
					const link = menuItemContainer.querySelector('a.menu-item-text');
					if (link) {
						const href = link.getAttribute('href');
						if (!href || href === '#' || href === '') {
							link.addEventListener('click', (e) => {
								e.preventDefault();
								this.openSubmenu(submenuIndex);
							});
						}
					}

					// Hide the original sub-menu in the main panel
					subMenu.style.display = 'none';
				}
			});

			this.submenus = submenus;
		},

		openSubmenu(index) {
			this.prevSubmenu = this.activeSubmenu;
			this.activeSubmenu = index;
		},

		goBack() {
			this.prevSubmenu = this.activeSubmenu;
			this.activeSubmenu = null;

			// Clean up prevSubmenu after transition
			setTimeout(() => {
				this.prevSubmenu = null;
			}, 350);
		}
	}));

	// =========================================================================
	// Mobile Menu Store (for open/close state)
	// =========================================================================

	Alpine.store('mobileMenu', {
		isOpen: false,

		toggle() {
			this.isOpen = !this.isOpen;
			if (this.isOpen) {
				document.body.style.overflow = 'hidden';
			} else {
				document.body.style.overflow = '';
			}
		},

		close() {
			this.isOpen = false;
			document.body.style.overflow = '';
		}
	});
});

// =============================================================================
// Desktop Mega Menu Hover
// =============================================================================

class MegaMenuManager {
	constructor() {
		this.menuItems = document.querySelectorAll('.primary__menu-container > ul > .wrapper-level-0');
		this.hoverDelay = 150;
		this.hoverTimeout = null;
		this.init();
	}

	init() {
		this.menuItems.forEach(item => {
			const submenu = item.querySelector('.sub-menu');
			if (!submenu) return;

			item.addEventListener('mouseenter', () => {
				clearTimeout(this.hoverTimeout);
				this.hoverTimeout = setTimeout(() => {
					this.openMegaMenu(item, submenu);
				}, this.hoverDelay);
			});

			item.addEventListener('mouseleave', () => {
				clearTimeout(this.hoverTimeout);
				this.hoverTimeout = setTimeout(() => {
					this.closeMegaMenu(item, submenu);
				}, this.hoverDelay);
			});

			const link = item.querySelector('.menu-item-text');
			link?.addEventListener('focus', () => this.openMegaMenu(item, submenu));

			item.addEventListener('keydown', (e) => {
				if (e.key === 'Escape') {
					this.closeMegaMenu(item, submenu);
					link?.focus();
				}
			});
		});
	}

	openMegaMenu(item, submenu) {
		this.closeAllMenus();
		item.classList.add('active');
		submenu.setAttribute('aria-hidden', 'false');
	}

	closeMegaMenu(item, submenu) {
		item.classList.remove('active');
		submenu.setAttribute('aria-hidden', 'true');
	}

	closeAllMenus() {
		this.menuItems.forEach(item => {
			const submenu = item.querySelector('.sub-menu');
			if (submenu) {
				item.classList.remove('active');
				submenu.setAttribute('aria-hidden', 'true');
			}
		});
	}
}

if (document.readyState === 'loading') {
	document.addEventListener('DOMContentLoaded', () => new MegaMenuManager());
} else {
	new MegaMenuManager();
}
